import type { FC, ReactNode } from 'react'

import {
    ArrowUpRight,
    Bell,
    Bookmark,
    BriefcaseBusiness,
    ChevronRight,
    GraduationCap,
    Play,
    Search
} from 'lucide-react'
import Container from '@/components/landing/container'

import { LINKS } from '@/constants/landing'

const APP_FEATURES = [
    { label: "Industry-focused training", icon: GraduationCap },
    { label: "Job placement support", icon: BriefcaseBusiness },
    { label: "Search and save jobs", icon: Bookmark }
]

const PhoneMockup: FC = (): ReactNode => {
    return (
        <div aria-hidden='true' className='relative mx-auto w-60 rotate-3 rounded-[2.75rem] border-[10px] border-brand-navy bg-cream p-3.5 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.6)]'>
            <div className='mx-auto mb-4 h-1.5 w-16 rounded-full bg-brand-navy/15' />

            <div className='flex items-center justify-between px-1'>
                <span className='font-heading text-sm font-bold text-primary'>Skillkafe</span>
                <Bell className='size-4 text-muted-foreground' />
            </div>

            <div className='mt-3 rounded-2xl bg-primary p-3.5 text-white'>
                <p className='text-[10px] font-semibold uppercase tracking-wider text-white/70'>Continue learning</p>
                <p className='mt-1 text-sm font-semibold'>BFSI Aspire</p>

                <div className='mt-3 h-1.5 rounded-full bg-white/20'>
                    <div className='h-full w-2/3 rounded-full bg-brand-orange' />
                </div>
            </div>

            <div className='mt-3 space-y-2'>
                <div className='flex items-center gap-2.5 rounded-xl bg-white p-2.5 shadow-sm'>
                    <span className='size-7 rounded-lg bg-brand-orange/20' />
                    <span className='flex-1 text-xs font-semibold text-foreground'>Auto Aspire</span>
                    <ChevronRight className='size-3.5 text-muted-foreground' />
                </div>

                <div className='flex items-center gap-2.5 rounded-xl bg-white p-2.5 shadow-sm'>
                    <span className='size-7 rounded-lg bg-brand-teal/20' />
                    <span className='flex-1 text-xs font-semibold text-foreground'>Customer Relationship</span>
                    <ChevronRight className='size-3.5 text-muted-foreground' />
                </div>
            </div>

            <div className='mt-3 rounded-xl bg-white p-2.5 shadow-sm'>
                <p className='text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>Find jobs</p>

                <div className='mt-2 flex items-center gap-2 rounded-lg bg-muted px-2.5 py-2'>
                    <Search className='size-3.5 text-muted-foreground' />
                    <span className='text-[11px] text-muted-foreground'>Search jobs near you</span>
                </div>
            </div>
        </div>
    )
}

const AppSection: FC = (): ReactNode => {
    return (
        <section id='app' aria-labelledby='app-title' className='py-20 sm:py-28'>
            <Container className='grid gap-6 lg:grid-cols-5'>
                <div className='reveal relative isolate overflow-hidden rounded-[2rem] bg-primary p-8 text-white sm:p-12 lg:col-span-3'>
                    <div aria-hidden='true' className='absolute -right-24 -top-24 -z-10 size-80 rounded-full bg-brand-teal/30 blur-3xl' />
                    <div aria-hidden='true' className='absolute -bottom-32 left-1/4 -z-10 size-72 rounded-full bg-brand-orange/20 blur-3xl' />

                    <div className='grid items-center gap-12 md:grid-cols-[1fr_auto]'>
                        <div>
                            <p className='inline-flex items-center gap-2 text-sm font-semibold tracking-wide text-brand-teal-bright'>
                                <span aria-hidden='true' className='size-1.5 rounded-full bg-brand-orange' />
                                On your phone
                            </p>

                            <h2 id='app-title' className='mt-4 text-3xl font-semibold leading-tight sm:text-4xl'>
                                Learn on the go with the Skillkafe app
                            </h2>

                            <p className='mt-4 max-w-md leading-relaxed text-white/75'>
                                Unlock industry-focused training wherever you are, then search for jobs and save the ones you like.
                            </p>

                            <ul className='mt-7 space-y-3'>
                                {APP_FEATURES.map(feature => {
                                    const Icon = feature.icon

                                    return (
                                        <li key={feature.label} className='flex items-center gap-3 font-medium'>
                                            <span className='grid size-9 place-items-center rounded-xl bg-white/10 text-brand-teal-bright ring-1 ring-white/15'>
                                                <Icon className='size-4' aria-hidden='true' />
                                            </span>
                                            {feature.label}
                                        </li>
                                    )
                                })}
                            </ul>

                            <a
                                href={LINKS.playStore}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='mt-9 inline-flex items-center gap-3 rounded-2xl bg-white px-5 py-3 text-foreground shadow-lg transition-colors hover:bg-cream'
                            >
                                <span className='grid size-9 place-items-center rounded-xl bg-primary text-primary-foreground'>
                                    <Play className='ml-0.5 size-4' fill='currentColor' strokeWidth={0} aria-hidden='true' />
                                </span>

                                <span className='text-left leading-tight'>
                                    <span className='block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground'>Get it on</span>
                                    <span className='block font-heading text-lg font-semibold'>Google Play</span>
                                </span>

                                <span className='sr-only'>(opens in a new tab)</span>
                            </a>
                        </div>

                        <div className='hidden md:block'>
                            <PhoneMockup />
                        </div>
                    </div>
                </div>

                <div className='reveal relative isolate flex flex-col overflow-hidden rounded-[2rem] border border-border bg-sand p-8 sm:p-12 lg:col-span-2'>
                    <div aria-hidden='true' className='absolute -bottom-20 -right-20 -z-10 size-64 rounded-full border-[32px] border-brand-orange/10' />

                    <span className='grid size-14 place-items-center rounded-2xl bg-brand-orange text-brand-navy shadow-lg shadow-brand-orange/30'>
                        <GraduationCap className='size-7' aria-hidden='true' />
                    </span>

                    <p className='mt-8 text-sm font-semibold text-brand-orange-ink'>
                        SkillKafe Degrees
                    </p>

                    <h3 className='mt-2 text-2xl font-semibold leading-snug text-foreground sm:text-3xl'>
                        Want a degree too?
                    </h3>

                    <p className='mt-4 leading-relaxed text-muted-foreground'>
                        Our sister platform EduKafe offers UGC approved undergraduate and postgraduate degree programmes.
                    </p>

                    <a
                        href={LINKS.eduKafe}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='mt-auto inline-flex w-fit items-center gap-1.5 pt-10 font-semibold text-primary underline-offset-4 hover:underline'
                    >
                        Explore degrees on EduKafe
                        <ArrowUpRight className='size-4' aria-hidden='true' />
                        <span className='sr-only'>(opens in a new tab)</span>
                    </a>
                </div>
            </Container>
        </section>
    )
}

export default AppSection
